import { apiService } from '../services/apiService';
import { frontendConfig } from '../config';

/**
 * Generate a unique session ID for a training session
 * Format: trackName-UUID
 */
export const generateTrainingSessionId = (trackName: string): string => {
  const uuid = crypto.randomUUID();
  // Sanitize track name for use in ID (remove special chars, replace spaces with hyphens)
  const sanitizedName = trackName
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .substring(0, 30); // Limit length

  return `${sanitizedName}-${uuid}`;
};

/**
 * Navigate to training session in frontend via SSO
 * This creates an SSO token and opens the frontend training session page
 */
export const navigateToTrainingSession = async (
  trackId: string,
  trackName: string
): Promise<void> => {
  try {
    // Generate unique session ID
    const sessionId = generateTrainingSessionId(trackName);

    // Generate SSO token for training mode
    const response = await apiService.post('/auth/generate-sso-token', {
      mode: 'training',
      trackId,
      sessionId,
      trackName
    });

    if (!response.success || !response.data.ssoToken) {
      throw new Error('Failed to generate SSO token');
    }

    const ssoToken = response.data.ssoToken;

    // Construct frontend training session URL
    const frontendUrl = frontendConfig.baseURL;
    const trainingUrl = `${frontendUrl}/sso?token=${encodeURIComponent(ssoToken)}&mode=training&sessionId=${encodeURIComponent(sessionId)}&trackId=${encodeURIComponent(trackId)}`;

    // Open in new tab
    window.open(trainingUrl, '_blank');

    console.log('🎓 Navigating to training session:', {
      trackId,
      sessionId,
      trackName
    });
  } catch (error: any) {
    console.error('❌ Error navigating to training session:', error);
    throw new Error(error.message || 'Failed to start training session');
  }
};

/**
 * Create a training session record in the database
 */
export const createTrainingSession = async (
  trackId: string,
  sessionId: string
): Promise<any> => {
  try {
    const response = await apiService.post('/training/sessions', {
      trackId,
      sessionId,
      status: 'in_progress',
      startedAt: new Date().toISOString()
    });

    if (!response.success) {
      throw new Error('Failed to create training session');
    }

    return response.data;
  } catch (error: any) {
    console.error('❌ Error creating training session:', error);
    throw error;
  }
};
